"use client";

import {useEffect, useRef} from "react";
import {toast} from "sonner";
import dynamic from "next/dynamic";
import {siteConfig} from "@/config/site";
import {AuthErrorCode} from "@/config/auth/errors";
import {SIGNUP_REFUSED_COOKIE} from "@/features/auth/lib/auth";

/**
 * Reads and clears the cookie createUser sets when it refuses a signup, so
 * the verdict is shown once and never taints a later, unrelated error.
 */
function consumeSignupRefusedCookie(): boolean {
    const refused = document.cookie.includes(`${SIGNUP_REFUSED_COOKIE}=true`);
    if (refused) {
        document.cookie = `${SIGNUP_REFUSED_COOKIE}=; max-age=0; path=/`;
    }
    return refused;
}

function LoginErrorHandler() {
    const hasRun = useRef(false);

    useEffect(() => {
        if (hasRun.current) return;
        hasRun.current = true;

        const url = new URL(window.location.href);
        const err = url.searchParams.get("error");

        if (err) {
            const signupRefused = consumeSignupRefusedCookie();

            if (err === AuthErrorCode.OAuthAccountNotLinked) {
                toast.error(
                    "This account is already linked to another user. Please log in with the correct account or contact support if you believe this is an error.",
                    {
                        duration: 5000,
                        description: "If you need assistance, please reach out to our support team.",
                    }
                );
            } else if (err === AuthErrorCode.MsTokenFail || (err === AuthErrorCode.Configuration && signupRefused)) {
                // A deliberate refusal by the email or fingerprint gate.
                toast.error(
                    `An error occurred. If the problem persists, please contact ${siteConfig.mailSupport}. You may be blocked by our anti-spam filter; consider turning off any VPNs.`,
                    {
                        duration: 5000,
                    }
                );
            } else if (err === AuthErrorCode.CaptchaFailed) {
                toast.error("Captcha verification failed. Please try again.", {duration: 5000});
            } else if (err === AuthErrorCode.QqRequiresWechat) {
                toast.error("QQ accounts sign up through WeChat. Please use the WeChat option.", {duration: 5000});
            } else {
                // Everything else Auth.js folds into error=Configuration is a
                // fault on our side or a lost cookie, never a spam verdict.
                toast.error(
                    `Sign in failed. Please try again, and contact ${siteConfig.mailSupport} if the problem persists.`,
                    {
                        duration: 5000,
                    }
                );
            }

            // Remove the error param without reloading
            url.searchParams.delete("error");
            window.history.replaceState({}, document.title, url.toString());
        }
    }, []);

    return null;
}

const DynamicLoginErrorHandler = dynamic(() => Promise.resolve(LoginErrorHandler),
    {
        ssr: false, // Ensure this component is only rendered on the client side
    }
);

export default DynamicLoginErrorHandler;